import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Globe, FileSearch, Wrench, BarChart3, ChevronDown, CheckCircle, XCircle, Play, ArrowRight, Download, Zap, Eye, RefreshCw, Cpu } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoModal from '../components/VideoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const HERO_IMG  = `${BACKEND_URL}/api/builder/media/files/products/aijoomla-security-suite/images/aijoomla_security_hero.png`;
const FREE_BOX  = `${BACKEND_URL}/api/builder/media/files/products/aijoomla-security-suite/images/aijoomla_security_free_box.png`;
const PRO_BOX   = `${BACKEND_URL}/api/builder/media/files/products/aijoomla-security-suite/images/aijoomla_security_pro_box.png`;

const DEMO_VIDEO_URL  = 'https://customer-assets.emergentagent.com/job_960a7272-fe11-4b2f-a6b4-7f0bd404139b/artifacts/csr2700j_AI%20Joomla%20Security%20Suite-final.mp4';
const DEMO_COVER_URL  = HERO_IMG;

// ── Feature cards ──────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: Shield,      title: 'Brute-Force Protection',     desc: 'Automatically ban IPs that exceed failed-login attempts. Configurable threshold, lockout duration, and full audit log.' },
  { icon: Globe,       title: 'Country Blocking',           desc: 'Block entire countries by ISO code or run in allow-only mode. Works at the PHP layer — no server config required.' },
  { icon: Lock,        title: 'IP Blocklist & Allowlist',   desc: 'Permanently block or whitelist individual IPs and CIDR ranges. Manual overrides always respected.' },
  { icon: Cpu,         title: 'Security Response Headers',  desc: 'One toggle enables X-Frame-Options, X-Content-Type-Options, Referrer-Policy, HSTS, and a configurable CSP on every response.' },
  { icon: FileSearch,  title: 'File Integrity Scanner',     desc: 'Generate a baseline and detect modified, added, or deleted files. View a line-by-line diff inside the Joomla admin.' },
  { icon: Eye,         title: 'Official Core Check',        desc: 'No baseline required. Compare core files against Joomla\'s official GitHub source — works even on already-compromised sites.' },
  { icon: RefreshCw,   title: 'One-Click Core Restore',     desc: 'Replace any hacked core file with the official version fetched live from GitHub. Composer vendor libs correctly identified.' },
  { icon: Wrench,      title: 'Fix .htaccess',              desc: 'Instantly reset .htaccess to the official Joomla default for your version. Previous file auto-backed up before overwriting.' },
  { icon: BarChart3,   title: 'Security Dashboard',         desc: 'Live engine status, active ban count, recent events, and one-click access to all security tools — all in one panel.' },
];

// ── Free vs Pro comparison ─────────────────────────────────────────────────────
const COMPARE = [
  { feature: 'Brute-Force IP Auto-Ban',          free: true,  pro: true },
  { feature: 'Country Blocking',                 free: true,  pro: true },
  { feature: 'Manual IP Blocklist & Allowlist',  free: true,  pro: true },
  { feature: 'Security Response Headers',        free: true,  pro: true },
  { feature: 'File Integrity Scanner',           free: true,  pro: true },
  { feature: 'Official Core Check (no baseline)',free: true,  pro: true },
  { feature: 'One-Click Core File Restore',      free: true,  pro: true },
  { feature: 'Fix .htaccess',                    free: true,  pro: true },
  { feature: 'Security Dashboard',               free: true,  pro: true },
  { feature: 'Audit Log',                        free: true,  pro: true },
  { feature: 'Priority Support',                 free: false, pro: true },
  { feature: 'Automatic Joomla Update Notifications', free: false, pro: true },
  { feature: 'Future PRO-Exclusive Modules',     free: false, pro: true },
];

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Does it work with Joomla 4, 5, and 6?', a: 'Yes — AI Joomla Security Suite is tested and supported on Joomla 4.x, 5.x, and 6.x with PHP 8.0+.' },
  { q: 'What\'s the difference between the baseline scan and the Official Core Check?', a: 'The baseline scan compares files against a snapshot you took when the site was clean. The Official Core Check compares directly against Joomla\'s GitHub source — so it works even if you never set a baseline, or if the site was already hacked before you installed the scanner.' },
  { q: 'Will "Fix .htaccess" overwrite my custom redirect rules?', a: 'Yes — it replaces the entire file with the official Joomla default. Your old file is automatically backed up as .htaccess.aijsbak.{timestamp} in the Joomla root before overwriting, so you can recover your custom rules.' },
  { q: 'Does the country blocker work on shared hosting?', a: 'Yes. Blocking happens in PHP via the system plugin — no Apache or Nginx config access required.' },
  { q: 'Can I restore a file that\'s in libraries/vendor/?', a: 'Not via one-click restore, because Composer vendor libraries are not stored individually on Joomla\'s GitHub. The scanner identifies them and directs you to reinstall via the Joomla Update component instead.' },
  { q: 'Do the security headers break my Joomla admin?', a: 'All headers are individually toggled (default: X-Frame-Options, nosniff, and Referrer-Policy ON; HSTS and CSP OFF). CSP has a Report-Only mode so you can safely tune your policy in the browser console before enforcing it.' },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-5 flex justify-between items-center gap-4 group">
        <span className="text-white font-medium group-hover:text-blue-400 transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-5 text-gray-400 leading-relaxed text-sm">{a}</p>}
    </div>
  );
}


export default function AiJoomlaSecurityPage() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <div className="min-h-screen bg-bots-dark text-white font-sans">
      <Header />
      <VideoModal
        isOpen={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoUrl={DEMO_VIDEO_URL}
        title="AI Joomla Security Suite — Full Demo"
      />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_0%,_rgba(6,182,212,0.15),_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-6">
              <Shield className="w-3.5 h-3.5" /> Joomla Security Suite
            </div>
            <h1 className="text-5xl sm:text-6xl font-extrabold leading-tight mb-6">
              AI Joomla<br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Security Suite</span>
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-xl">
              Enterprise-grade security for Joomla 4, 5 &amp; 6. Blocks threats in real time,
              monitors file integrity, and fixes vulnerabilities in one click — no server access needed.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/extensions/ai-joomla-security-suite-pro"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold transition-all hover:shadow-lg hover:shadow-cyan-500/30"
                data-testid="cta-buy-pro"
              >
                <Zap className="w-4 h-4" /> Buy PRO
              </Link>
              <Link
                to="/extensions/ai-joomla-security-suite-free"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:border-white/40 text-white font-medium transition-all"
                data-testid="cta-download-free"
              >
                <Download className="w-4 h-4" /> Download FREE
              </Link>
              <button
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-cyan-500/40 hover:border-cyan-400 text-cyan-400 hover:text-white font-medium transition-all hover:bg-cyan-500/10"
                data-testid="cta-watch-demo"
              >
                <Play className="w-4 h-4" fill="currentColor" /> Watch Demo
              </button>
            </div>
            <Link
              to="/security-scan"
              className="inline-flex items-center gap-2 mt-4 text-sm text-red-400 hover:text-red-300 font-medium group"
              data-testid="cta-free-site-scan"
            >
              <Zap className="w-3.5 h-3.5" /> Not sure if you're at risk? Run a free 60-second site scan
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <div className="flex flex-wrap gap-4 mt-8 text-xs text-gray-400">
              {['Joomla 4, 5 & 6','PHP 8.0+','Apache · LiteSpeed · Nginx','No server access needed'].map(b => (
                <span key={b} className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" />{b}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt="AI Joomla Security Suite" className="w-full rounded-2xl shadow-2xl shadow-black/50 border border-white/10" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl" />
          </div>
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
      <section className="bg-white/5 border-y border-white/10 py-6">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[['9+','Security Features'],['3','Joomla Versions Supported'],['5','Response Headers Managed'],['1-Click','Core File Restore']].map(([n, l]) => (
            <div key={l}>
              <p className="text-3xl font-extrabold text-cyan-400">{n}</p>
              <p className="text-gray-400 text-sm mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES GRID ─────────────────────────────────────────────────── */}
      <section className="py-24 max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Everything you need to lock down Joomla</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">From real-time threat blocking to deep file scanning and instant remediation — all managed from inside the Joomla admin panel.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="group bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-cyan-400/40 hover:bg-cyan-400/5 transition-all">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-all">
                <Icon className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-2">{title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── VIDEO SECTION ─────────────────────────────────────────────────── */}
      <section className="bg-white/3 border-y border-white/10 py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">See it in action</h2>
            <p className="text-gray-400">Watch how AI Joomla Security protects a real site — installation, configuration, and threat response in under 4 minutes.</p>
          </div>

          {/* Single video thumbnail with play button */}
          <div
            className="relative rounded-2xl overflow-hidden cursor-pointer group aspect-[1024/687] border border-white/10 shadow-2xl shadow-black/50 bg-[#0a0e1a]"
            onClick={() => setVideoOpen(true)}
            data-testid="demo-video-thumbnail"
            role="button"
            aria-label="Watch the AI Joomla Security Suite demo"
          >
            {/* Cover image */}
            <img
              src={DEMO_COVER_URL}
              alt="AI Joomla Security Suite demo"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />

            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />

            {/* Play button */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-sm border-2 border-white/40 flex items-center justify-center group-hover:bg-white/25 group-hover:scale-110 transition-all duration-300 shadow-2xl">
                <Play className="w-9 h-9 text-white ml-1.5" fill="white" />
              </div>
              <span className="text-white/80 text-sm font-medium tracking-wide uppercase bg-black/30 backdrop-blur-sm px-4 py-1.5 rounded-full border border-white/20">
                Watch Full Demo
              </span>
            </div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-4">4:00 mins &nbsp;·&nbsp; Installation · Configuration · Threat Blocking · File Scanner · Maintenance Tools</p>
        </div>
      </section>

      {/* ── FREE vs PRO ──────────────────────────────────────────────────── */}
      <section className="py-24 max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Free vs Pro</h2>
          <p className="text-gray-400">All core security features are free, forever. PRO adds priority support and future exclusive modules.</p>
        </div>
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-3 bg-white/5 text-sm font-bold text-center py-4 border-b border-white/10">
            <div className="text-left pl-6 text-gray-300">Feature</div>
            <div className="text-gray-300">FREE</div>
            <div className="text-cyan-400">PRO</div>
          </div>
          {COMPARE.map(({ feature, free, pro }) => (
            <div key={feature} className="grid grid-cols-3 text-sm text-center py-3.5 border-b border-white/5 hover:bg-white/3 transition-colors">
              <div className="text-left pl-6 text-gray-300">{feature}</div>
              <div className="flex justify-center">{free ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-gray-600" />}</div>
              <div className="flex justify-center">{pro  ? <CheckCircle className="w-4 h-4 text-cyan-400"    /> : <XCircle className="w-4 h-4 text-gray-600" />}</div>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="rounded-2xl border border-white/15 p-6 text-center bg-white/3">
            <img src={FREE_BOX} alt="Free edition" className="w-32 mx-auto mb-4 rounded-xl" />
            <h3 className="text-xl font-bold text-white mb-2">FREE Edition</h3>
            <p className="text-gray-400 text-sm mb-4">All core security features, forever free.</p>
            <Link to="/extensions/ai-joomla-security-suite-free" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 hover:border-white/40 text-white text-sm font-medium transition-all" data-testid="compare-free-btn">
              <Download className="w-4 h-4" /> Download Free
            </Link>
          </div>
          <div className="rounded-2xl border border-cyan-400/30 p-6 text-center bg-cyan-400/5">
            <img src={PRO_BOX} alt="Pro edition" className="w-32 mx-auto mb-4 rounded-xl" />
            <h3 className="text-xl font-bold text-white mb-2">PRO Edition</h3>
            <p className="text-gray-400 text-sm mb-4">Priority support + future PRO-exclusive modules.</p>
            <Link to="/extensions/ai-joomla-security-suite-pro" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-bold transition-all" data-testid="compare-pro-btn">
              <ArrowRight className="w-4 h-4" /> Get PRO
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="bg-white/3 border-t border-white/10 py-24">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently asked questions</h2>
          {FAQS.map(faq => <FaqItem key={faq.q} {...faq} />)}
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
          <h2 className="text-4xl font-extrabold text-white mb-4">Ready to secure your Joomla site?</h2>
          <p className="text-gray-400 text-lg mb-8">Install in minutes. Works on any Joomla 4, 5, or 6 site with no server access required.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/extensions/ai-joomla-security-suite-pro"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-lg transition-all hover:shadow-xl hover:shadow-cyan-500/30"
              data-testid="final-cta-pro">
              <Zap className="w-5 h-5" /> Get PRO Now
            </Link>
            <Link to="/extensions/ai-joomla-security-suite-free"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 hover:border-white/40 text-white font-medium text-lg transition-all"
              data-testid="final-cta-free">
              <Download className="w-5 h-5" /> Download Free
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
